import React, { useState, useEffect } from 'react'
import { 
  Card, 
  List, 
  Pagination, 
  Spin, 
  Tag, 
  Typography, 
  Space, 
  Select,
  Input,
  Button,
  Empty,
  Divider
} from 'antd'
import { 
  BookOutlined, 
  UserOutlined, 
  CalendarOutlined,
  FileTextOutlined,
  SearchOutlined,
  FilterOutlined
} from '@ant-design/icons'
import { useNavigate, useSearchParams } from 'react-router-dom'
import CategoryTree from '../Category/CategoryTree'
import './BookList.css'

const { Title, Text } = Typography
const { Option } = Select

function BookList() {
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  
  // 书籍数据状态
  const [books, setBooks] = useState([])
  const [loading, setLoading] = useState(false)
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 20,
    total: 0
  })
  
  // 分类和筛选状态
  const [categories, setCategories] = useState([])
  const [selectedCategory, setSelectedCategory] = useState(searchParams.get('category') || '')
  const [searchKeyword, setSearchKeyword] = useState(searchParams.get('search') || '')
  const [sortField, setSortField] = useState(searchParams.get('sort') || 'title')
  
  // 获取书籍列表
  useEffect(() => {
    fetchBooks()
    fetchCategories()
  }, [])
  
  // 当筛选条件变化时重新获取数据
  useEffect(() => {
    fetchBooks()
  }, [selectedCategory, searchKeyword, sortField, pagination.current])
  
  const fetchBooks = async () => {
    setLoading(true)
    
    try {
      const params = new URLSearchParams({
        limit: pagination.pageSize.toString(),
        offset: ((pagination.current - 1) * pagination.pageSize).toString(),
        sort: sortField
      })
      
      if (selectedCategory) {
        params.append('category', selectedCategory)
      }
      
      if (searchKeyword) {
        params.append('search', searchKeyword)
      }
      
      const response = await fetch(`/api/books?${params}`)
      const data = await response.json()
      
      if (response.ok) {
        setBooks(data.books || [])
        setPagination(prev => ({
          ...prev,
          total: data.pagination?.total || 0
        }))
      }
    } catch (error) {
      console.error('获取书籍列表失败:', error)
    } finally {
      setLoading(false)
    }
  }
  
  const fetchCategories = async () => {
    try {
      const response = await fetch('/api/categories')
      const data = await response.json()
      
      if (response.ok) {
        setCategories(data.categories || [])
      }
    } catch (error) {
      console.error('获取分类失败:', error)
    }
  }
  
  const handlePageChange = (page, pageSize) => {
    setPagination(prev => ({ ...prev, current: page, pageSize }))
  }
  
  const handleCategorySelect = (categoryPath) => {
    setSelectedCategory(categoryPath)
    setPagination(prev => ({ ...prev, current: 1 }))
    
    // 更新URL参数
    const newParams = new URLSearchParams(searchParams)
    if (categoryPath) {
      newParams.set('category', categoryPath)
    } else {
      newParams.delete('category')
    }
    setSearchParams(newParams)
  }
  
  const handleSearch = (value) => {
    setSearchKeyword(value)
    setPagination(prev => ({ ...prev, current: 1 }))
    
    // 更新URL参数
    const newParams = new URLSearchParams(searchParams)
    if (value) {
      newParams.set('search', value)
    } else {
      newParams.delete('search')
    }
    setSearchParams(newParams)
  }
  
  const handleSortChange = (value) => {
    setSortField(value)
    setPagination(prev => ({ ...prev, current: 1 }))
    
    // 更新URL参数
    const newParams = new URLSearchParams(searchParams)
    newParams.set('sort', value)
    setSearchParams(newParams)
  }
  
  const goToBook = (bookId) => {
    navigate(`/books/${bookId}`)
  }
  
  return (
    <div className="book-list-page">
      <div className="book-list-container">
        {/* 页面标题 */}
        <div className="page-header">
          <Title level={2}>
            <BookOutlined /> 古籍书库
          </Title>
          <Text type="secondary">浏览和搜索中国古代文献</Text>
        </div>
        
        <div className="book-list-content">
          {/* 左侧分类导航 */}
          <div className="sidebar">
            <Card className="sidebar-card">
              <div className="sidebar-header">
                <Title level={5}>
                  <FilterOutlined /> 分类浏览
                </Title>
              </div>
              <CategoryTree 
                categories={categories}
                selectedCategory={selectedCategory}
                onSelect={handleCategorySelect}
              />
            </Card>
          </div>
          
          {/* 右侧书籍列表 */}
          <div className="main-content">
            {/* 筛选和搜索区域 */}
            <Card className="filter-card">
              <div className="filter-section">
                <Input.Search
                  placeholder="搜索书名或作者..."
                  allowClear
                  enterButton={<SearchOutlined />}
                  size="large"
                  value={searchKeyword}
                  onChange={(e) => setSearchKeyword(e.target.value)}
                  onSearch={handleSearch}
                  className="search-input"
                />
                
                <Select
                  value={sortField}
                  onChange={handleSortChange}
                  size="large"
                  className="sort-select"
                >
                  <Option value="title">按书名排序</Option>
                  <Option value="author">按作者排序</Option>
                  <Option value="created_at">按时间排序</Option>
                  <Option value="file_size">按大小排序</Option>
                </Select>
              </div>
              
              {/* 当前筛选条件 */}
              {(selectedCategory || searchKeyword) && (
                <div className="current-filters">
                  <Space wrap>
                    {selectedCategory && (
                      <Tag 
                        closable 
                        onClose={() => handleCategorySelect('')}
                      >
                        分类: {selectedCategory}
                      </Tag>
                    )}
                    {searchKeyword && (
                      <Tag 
                        closable 
                        onClose={() => handleSearch('')}
                      >
                        搜索: {searchKeyword}
                      </Tag>
                    )}
                  </Space>
                </div>
              )}
            </Card>
            
            {/* 书籍列表 */}
            <Spin spinning={loading}>
              {books.length > 0 ? (
                <>
                  <List
                    className="books-list"
                    dataSource={books}
                    renderItem={(book) => (
                      <List.Item className="book-item">
                        <Card 
                          hoverable
                          className="book-card"
                          onClick={() => goToBook(book.id)}
                        >
                          <div className="book-header">
                            <Title level={5} className="book-title">
                              <BookOutlined /> {book.title}
                            </Title>
                            <div className="book-meta">
                              {book.author && (
                                <Tag icon={<UserOutlined />} color="blue">
                                  {book.author}
                                </Tag>
                              )}
                              {book.dynasty && (
                                <Tag icon={<CalendarOutlined />} color="green">
                                  {book.dynasty}
                                </Tag>
                              )}
                              {book.category_name && (
                                <Tag color="orange">
                                  {book.category_name}
                                </Tag>
                              )}
                            </div>
                          </div>
                          
                          <div className="book-stats">
                            <Space>
                              <Text type="secondary">
                                <FileTextOutlined /> {book.page_count || 0} 页
                              </Text>
                              <Text type="secondary">
                                {(book.file_size / 1024 / 1024).toFixed(1)} MB
                              </Text>
                            </Space>
                          </div>
                        </Card>
                      </List.Item>
                    )}
                  />
                  
                  <div className="pagination-container">
                    <Pagination
                      current={pagination.current}
                      total={pagination.total}
                      pageSize={pagination.pageSize}
                      showSizeChanger
                      showQuickJumper
                      showTotal={(total, range) => 
                        `第 ${range[0]}-${range[1]} 条，共 ${total} 条结果`
                      }
                      onChange={handlePageChange}
                      onShowSizeChange={handlePageChange}
                    />
                  </div>
                </>
              ) : !loading ? (
                <Empty
                  description="没有找到相关书籍"
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                />
              ) : null}
            </Spin>
          </div>
        </div>
      </div>
    </div>
  )
}

export default BookList